import { useCallback, useEffect, useRef } from 'react';
import { trackEvent, TypeTraceEvent, TypeTraceEventData } from '../../utils/tracking';

interface TrackCancellable {
  (): void;
  cancel: () => void;
}

export default function useTrackingEventTimed<E extends TypeTraceEvent>(
  event: E,
  data: TypeTraceEventData[E],
  time = 3000,
): TrackCancellable {
  const refTimer = useRef<number | null>(null);

  const track = useCallback(() => {
    if (refTimer.current === null) {
      refTimer.current = window.setTimeout(() => {
        trackEvent(event, data);
        refTimer.current = null;
      }, time);
    }
  }, [data, event, time]);

  const trackCancellable = track as TrackCancellable;

  trackCancellable.cancel = () => {
    if (refTimer.current !== null) {
      window.clearTimeout(refTimer.current);
      refTimer.current = null;
    }
  };

  useEffect(
    () => () => {
      trackCancellable.cancel();
    },
    [trackCancellable],
  );

  return trackCancellable;
}
